import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import ScienceScoringListing from '../../components/Scoring/ScienceScoring/ScienceScoringListing';
import ScienceScoringForm from '../../components/Scoring/ScienceScoring/ScienceScoringForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import ScienceScoringReferenceForm from '../../components/Scoring/ScienceScoring/ScienceScoringReferenceForm';

const ScienceScoring = () => {
    const toast = useRef(null);
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRefModalOpen, setIsRefModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const today = new Date().toISOString().split('T')[0];
    const currentYear = new Date().getFullYear();
    const defaultFromDate = `${currentYear}-01-01`;
    const defaultToDate = `${currentYear}-12-31`;

    const initialForm = { ScoringContent: 'All', ItemStatusFilter: 'All', Coefficient: 1, FromDate: defaultFromDate, ToDate: defaultToDate };
    const [formData, setFormData] = useState(initialForm);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }

            const currentUser = JSON.parse(userStr);
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/sciencescoring?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders()
                }
            );

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            if (res.ok) {
                setData(await res.json());
            }
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu!' });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring`, {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id: id.toString(), status: newStatus })
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            if (res.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? 'Đã gửi yêu cầu duyệt!' : 'Đã hủy yêu cầu duyệt!'
                });
                fetchData(true);
            } else {
                const errData = await res.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errData.message || 'Thao tác thất bại!' });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể kết nối máy chủ' });
        }
    };

    const handleBulkUpdateStatus = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đang chỉnh sửa') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring`, {
                    method: 'PATCH',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id: id.toString(), status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bản ghi đã khóa, không thể xóa!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa bản ghi tính điểm này?',
            accept: async () => {
                try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring?id=${id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (res.status === 401 || res.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (res.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa bản ghi' });
                        fetchData(true);
                    } else {
                        const errData = await res.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errData.message || 'Xóa thất bại!' });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ' });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bản ghi đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} bản ghi tính điểm đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring?id=${id}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleEdit = (item) => {
        if (item.Status === 'Đã gửi duyệt' || item.Status === 'Đã duyệt') {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bảng kê đã khóa, không thể chỉnh sửa!', life: 3000 });
            return;
        }

        setFormData({
            ...initialForm,
            ...item,
            FromDate: item.FromDate ? item.FromDate.split('T')[0] : defaultFromDate,
            ToDate: item.ToDate ? item.ToDate.split('T')[0] : defaultToDate
        });
        setIsModalOpen(true);
    };

    const handleSaveResult = async (finalData) => {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
            handleLogout();
            return;
        }

        const currentUser = JSON.parse(userStr);
        finalData.UserId = currentUser.Id;

        if (!finalData.Id) {
            const finalFrom = finalData.FromDate;
            const finalTo = finalData.ToDate;

            const existingItem = data.find(d => {
                if (!d.FromDate || !d.ToDate) return false;
                const dFrom = d.FromDate.split('T')[0];
                const dTo = d.ToDate.split('T')[0];
                return dFrom === finalFrom && dTo === finalTo && d.Status !== 'Tham khảo';
            });

            if (existingItem && existingItem.Status !== 'Đã duyệt') {
                finalData.Id = existingItem.Id;
            }
        }

        const method = finalData.Id ? 'PUT' : 'POST';

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoring`, {
                method: method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(finalData)
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            let result;
            try { result = await res.json(); } catch (err) { result = {}; }

            if (res.ok && result.status !== 'error') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Lưu bảng kê thành công!' });
                setIsModalOpen(false);
                setFormData(initialForm);
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Lưu thất bại!' });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể kết nối máy chủ' });
        }
    };

    const exportToExcel = () => {
        if (data.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!' });
        }

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};

        const excelData = data.map((item, index) => ({
            'STT': index + 1,
            'Nội dung tính điểm': item.ScoringContent === 'All' ? 'Tất cả danh mục' : (item.ScoringContent || ''),
            'Bộ lọc trạng thái': item.ItemStatusFilter === 'All' ? 'Tất cả trạng thái' : (item.ItemStatusFilter || ''),
            'Cơ chế áp dụng': item.AppliedConfigVersionName || 'Mặc định',
            'Hệ số (A)': item.Coefficient || 1,
            'Điểm đạt được': Math.round(Number(item.AchievedScore || 0) * 100) / 100,
            'Từ ngày': item.FromDate ? new Date(item.FromDate).toLocaleDateString('vi-VN') : '',
            'Đến ngày': item.ToDate ? new Date(item.ToDate).toLocaleDateString('vi-VN') : '',
            'Ngày cập nhật': item.UpdatedDate || '',
            'Trạng thái': item.Status || 'Đang chỉnh sửa',
            'Người tạo': currentUser?.Email || ''
        }));

        excelData.push({
            'STT': '',
            'Nội dung tính điểm': 'TỔNG CỘNG:',
            'Bộ lọc trạng thái': `${data.length} Bản ghi`,
            'Cơ chế áp dụng': '',
            'Hệ số (A)': '',
            'Điểm đạt được': '',
            'Từ ngày': '',
            'Đến ngày': '',
            'Ngày cập nhật': '',
            'Trạng thái': '',
            'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "QuanLyTinhDiem");

        const wscols = [
            { wch: 5 },
            { wch: 30 },
            { wch: 20 },
            { wch: 45 },
            { wch: 10 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 25 }
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachTinhDiem_${today}.xlsx`);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2>QUẢN LÝ TÍNH ĐIỂM</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={() => { setFormData(initialForm); setIsModalOpen(true); }} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Tính điểm
                </button>
                <button className="btn-add-new" style={{ backgroundColor: '#f39c12', margin: 0 }} onClick={() => setIsRefModalOpen(true)}>
                    <i className="fa-solid fa-calculator"></i> Tính điểm tham khảo
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>
            </div>

            <ScienceScoringListing
                data={data}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSubmitStatus={(id) => handleUpdateStatus(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleUpdateStatus(id, 'Đang chỉnh sửa')}
                onBulkSend={(ids) => handleBulkUpdateStatus(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkUpdateStatus(ids, 'Đang chỉnh sửa')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
            />

            {isModalOpen && (
                <ScienceScoringForm
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    formData={formData}
                    setFormData={setFormData}
                    onSaveResult={handleSaveResult}
                    toast={toast}
                />
            )}

            {isRefModalOpen && (
                <ScienceScoringReferenceForm
                    isOpen={isRefModalOpen}
                    onClose={() => setIsRefModalOpen(false)}
                    toast={toast}
                    formData={formData}
                    setFormData={setFormData}
                    data={data}
                    fetchData={fetchData}
                />
            )}
        </div>
    );
};

export default ScienceScoring;